<template>
  <div class="chat-hero" :class="{ 'chat-hero--intro': playIntro, 'chat-hero--reduced': reducedMotion }">
    <div class="chat-hero__top">
      <span class="chat-hero__avatar">🤖</span>
      <div>
        <h3 class="chat-hero__title">¿Qué casa buscas hoy?</h3>
        <p class="chat-hero__sub">Escríbelo como se lo dirías a un amigo. Te enseño los inmuebles al instante.</p>
      </div>
    </div>

    <!-- Botones prominentes de acción rápida -->
    <div class="chat-hero__actions">
      <button
        type="button"
        class="chat-hero__action"
        v-for="a in actions"
        :key="a.label"
        @click="quick(a)"
      >
        <span class="chat-hero__action-ico">{{ a.emoji }}</span>
        <span class="chat-hero__action-label">{{ a.label }}</span>
      </button>
    </div>

    <ChatComposer :open-dock-on-send="true" :hide-chips="true" />

    <p class="chat-hero__hint">
      <span class="fa fa-bolt mr-1"></span> Respuesta inmediata · sin llamadas ni formularios
    </p>

    <a class="chat-hero__wa" :href="whatsappUrl" target="_blank" rel="noopener" @click="trackWhatsapp">
      <span class="fa fa-whatsapp"></span> ¿Prefieres WhatsApp? Escríbenos
    </a>
  </div>
</template>

<script>
import ChatComposer from './ChatComposer.vue'
import { buildWhatsappUrl } from '../../utils/whatsapp'
import { shouldPlayIntro, markIntroSeen, prefersReducedMotion } from '../../utils/chatIntro'

export default {
  name: 'ChatHero',
  components: { ChatComposer },
  data () {
    return {
      playIntro: false,
      reducedMotion: prefersReducedMotion(),
      actions: [
        { label: 'Buscar vivienda', emoji: '🏠', kind: 'send', text: 'Quiero buscar una vivienda' },
        { label: 'Local o nave', emoji: '🏢', kind: 'send', text: 'Busco un local o una nave' },
        { label: 'Vender el mío', emoji: '💼', kind: 'send', text: 'Quiero vender mi inmueble' },
        { label: 'Hablar con un asesor', emoji: '🧑‍💼', kind: 'send', text: 'Quiero que un asesor se ponga en contacto conmigo' }
      ]
    }
  },
  computed: {
    whatsappUrl () {
      return buildWhatsappUrl(this.$route)
    }
  },
  mounted () {
    if (shouldPlayIntro()) {
      this.playIntro = true
      markIntroSeen()
    }
  },
  methods: {
    trackWhatsapp () {
      if (window.gtag) window.gtag('event', 'whatsapp_click', { page: this.$route.fullPath })
    },
    quick (a) {
      if (a.kind === 'catchment') {
        this.$store.dispatch('chat/startCatchment')
      } else if (a.kind === 'visit') {
        this.$store.dispatch('chat/scheduleVisit', null)
      } else {
        this.$store.dispatch('chat/openPanel')
        this.$store.dispatch('chat/send', a.text)
      }
    }
  }
}
</script>

<style scoped>
.chat-hero {
  position: relative;
  background: #fff;
  border-radius: 24px;
  padding: 26px;
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.28);
  max-width: 680px;
  width: 100%;
  margin: 0 auto;
  text-align: left;
  border-top: 5px solid #E45726;
}

.chat-hero__top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
}
.chat-hero__avatar {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #FDEDE7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}
.chat-hero__title {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #1D262D;
  line-height: 1.15;
}
.chat-hero__sub {
  margin: 4px 0 0;
  color: #5A646C;
  font-size: 15px;
}

/* Botones prominentes */
.chat-hero__actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}
.chat-hero__action {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #F6F7F9;
  border: 2px solid transparent;
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
  color: #1D262D;
  cursor: pointer;
  text-align: left;
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.chat-hero__action:hover {
  background: #fff;
  border-color: #E45726;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(228, 87, 38, 0.18);
}
.chat-hero__action-ico { font-size: 22px; line-height: 1; }
.chat-hero__action-label { line-height: 1.1; }

.chat-hero__hint {
  margin: 14px 0 0;
  text-align: center;
  font-size: 12px;
  color: #98A0A6;
}

.chat-hero__wa {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 100px;
  background: #25D366;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  transition: background 0.15s ease, transform 0.15s ease;
}
.chat-hero__wa .fa { font-size: 22px; }
.chat-hero__wa:hover { background: #1ebe5a; color: #fff; transform: translateY(-1px); text-decoration: none; }

/* Intro una vez por visita: entrada + pulso */
.chat-hero--intro {
  animation: hero-in 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both, hero-pulse 1.8s ease 0.6s 1;
}
@keyframes hero-in {
  from { opacity: 0; transform: translateY(28px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes hero-pulse {
  0%, 100% { box-shadow: 0 22px 60px rgba(0, 0, 0, 0.28); }
  45% { box-shadow: 0 22px 60px rgba(0, 0, 0, 0.28), 0 0 0 10px rgba(228, 87, 38, 0.20); }
}
.chat-hero--intro.chat-hero--reduced { animation: hero-fade 0.4s ease both; }
@keyframes hero-fade { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .chat-hero--intro { animation: hero-fade 0.4s ease both; }
  .chat-hero__action { transition: none; }
}

@media (max-width: 767px) {
  .chat-hero { padding: 20px; border-radius: 18px; }
  .chat-hero__title { font-size: 21px; }
  .chat-hero__actions { grid-template-columns: 1fr 1fr; gap: 8px; }
  .chat-hero__action { padding: 12px; font-size: 14px; }
}
@media (max-width: 360px) {
  .chat-hero__actions { grid-template-columns: 1fr; }
}
</style>
